import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { T } from '@tolgee/react'
import { CheckIcon, ClockIcon } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'

type TimePickerProps = Readonly<{
  time?: string
  onChange: (value: string) => void
  title: string
  disabled?: boolean
  interval?: number
  use12Hour?: boolean
}>

type TimeOption = {
  value: string
  label: string
}

const get24HourValue = (h: number, m: number): string =>
  `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`

const get12HourLabel = (h: number, m: number): string => {
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h
  const period = h < 12 ? 'AM' : 'PM'
  return `${hour12}:${m.toString().padStart(2, '0')} ${period}`
}

const getTimeLabel = (h: number, m: number, use12Hour: boolean): string =>
  use12Hour ? get12HourLabel(h, m) : get24HourValue(h, m)

const generateTimeOptions = (interval: number = 30, use12Hour: boolean = false): TimeOption[] => {
  const options: TimeOption[] = []
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += interval) {
      const value = get24HourValue(h, m)
      const label = getTimeLabel(h, m, use12Hour)
      options.push({ value, label })
    }
  }
  return options
}

const formatTimeForDisplay = (time: string, use12Hour: boolean): string => {
  if (!time) return ''
  const [hours, minutes] = time.split(':').map(Number)
  if (use12Hour) {
    const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours
    const period = hours < 12 ? 'AM' : 'PM'
    return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`
  }
  return time
}

export const TimePicker = ({
  time,
  onChange,
  title,
  disabled,
  interval = 30,
  use12Hour = false,
}: TimePickerProps) => {
  const [open, setOpen] = useState(false)
  const selectedRef = useRef<HTMLButtonElement | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)

  const timeOptions = useMemo(() => generateTimeOptions(interval, use12Hour), [interval, use12Hour])

  const displayTime = useMemo(() => {
    return time ? formatTimeForDisplay(time, use12Hour) : ''
  }, [time, use12Hour])

  // Scroll to selected time when popover opens
  const handlePopoverOpenChange = (isOpen: boolean) => {
    setOpen(isOpen)
    if (isOpen && time && selectedRef.current) {
      selectedRef.current.scrollIntoView({
        block: 'center',
        behavior: 'instant',
      })
    }
  }

  // Also handle scroll when the selected ref is set
  const handleSelectedRef = (element: HTMLButtonElement | null) => {
    selectedRef.current = element
    if (open && element) {
      element.scrollIntoView({
        block: 'center',
        behavior: 'instant',
      })
    }
  }

  return (
    <Popover open={open} onOpenChange={handlePopoverOpenChange}>
      <PopoverTrigger asChild>
        <Button
          unstyled
          title={title}
          disabled={disabled}
          className='relative flex h-8 w-[164px] select-none items-center gap-2 whitespace-nowrap rounded border border-neutral-75 bg-neutral-white px-2 text-sm font-light text-neutral-500 shadow-none hover:bg-neutral-50 focus:outline-0 active:bg-neutral-50 disabled:bg-neutral-50'
        >
          <ClockIcon className='size-5 text-neutral-150' />
          {displayTime || <T keyName='schedule.filters.timePicker.label' />}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        sideOffset={4}
        className={cn('w-auto p-1', use12Hour ? 'w-[140px]' : 'w-[100px]')}
      >
        <div
          ref={scrollContainerRef}
          className='max-h-60 overflow-y-auto focus:outline-none'
          onWheel={(e) => e.stopPropagation()}
        >
          {timeOptions.map((option) => {
            const isSelected = option.value === time
            return (
              <button
                key={option.value}
                ref={isSelected ? handleSelectedRef : null}
                className={cn(
                  'relative flex w-full items-center rounded px-2 py-1.5 pr-8 text-left text-sm font-normal text-neutral-500 transition-colors hover:bg-grey-100 focus:outline-none',
                  isSelected && 'bg-neutral-50',
                )}
                onClick={() => {
                  onChange(option.value)
                  setOpen(false)
                }}
                type='button'
              >
                {option.label}
                {isSelected && (
                  <span className='absolute right-2 flex size-3.5 items-center justify-center'>
                    <CheckIcon className='text-mango-300' />
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
